import React from 'react';
import { Helmet } from 'react-helmet';

export const SITE_NAME = 'Nordic Security Alliance';
export const SITE_URL = 'https://www.nordicsec.fi';

// One place for the title format so every page is "Page | Nordic Security Alliance".
// A page without a real title (empty, the site name, or the placeholder
// "Etusivu") is just the site name. The CMS page title is separate from the
// menu label, so the front page can carry a descriptive title here while the
// menu still says "Etusivu".
export const formatTitle = (title) => {
  const plain = (title || '').replace(/&shy;/g, '').replace(/­/g, '').trim();
  if (!plain || plain === SITE_NAME || /^etusivu$/i.test(plain)) return SITE_NAME;
  return `${plain} | ${SITE_NAME}`;
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The CMS has placeholder descriptions like "Meistä - Nordic Security Alliance".
// Those are worse than the site default, so treat them as missing.
export const usableDescription = (description, title) => {
  const d = (description || '').replace(/\s+/g, ' ').trim();
  if (!d) return '';
  const t = (title || '').replace(/&shy;/g, '').replace(/­/g, '').trim();
  if (t) {
    const placeholder = new RegExp(`^${escapeRegExp(t)}\\s*[-|–]\\s*${escapeRegExp(SITE_NAME)}$`, 'i');
    if (placeholder.test(d)) return '';
  }
  if (d.length < 30) return '';
  return d;
};

// Strips tags from CMS HTML so it can be used in a meta description.
export const plainText = (html, max = 160) => {
  if (!html) return '';
  const text = String(html).replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return text.slice(0, max - 1).replace(/\s+\S*$/, '') + '…';
};

// Sets <title>, description, og:title/og:description and optional noindex.
// Nested deeper than the App-level Helmet, so it wins over the site defaults.
const PageMeta = ({ title, description, noindex = false, jsonLd = null }) => {
  const fullTitle = formatTitle(title);
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta property="og:title" content={fullTitle} />
      {description && <meta name="description" content={description} />}
      {description && <meta property="og:description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default PageMeta;
